import { TranslateService } from '@ngx-translate/core';
import { CookieService } from 'ngx-cookie-service';
import { firstValueFrom } from 'rxjs';
import { MmuUserService } from '../auth/user-service/mmu-user.service';
import { MmuCookiePreferencesService } from '../cookies/service/mmu-cookie-preferences.service';
import { FALLBACK_LANGUAGE_LOCALE, PREFERRED_LANGUAGE_COOKIE_NAME } from './mmu-supported-languages';
import moment from 'moment';

/**
 * Some moment locales are not fully mapped to locale string, just the first part is used.
 * We know it is the case for de and us, no de-DE.js file exists, so we map it to de.js
 *
 * @param input language
 */
export const mapLocaleToMomentjsLocale = (input: string | undefined): string => {
  if (!input) {
    return 'en';
  }

  if (input === 'de-DE') { return 'de'; }
  if (input === 'fr-FR') { return 'fr'; }
  if (input === 'nl-NL') { return 'nl'; }
  if (input === 'en-US') { return 'en'; }

  // return full lowercase for: en-gb, en-ie, de-ch, de-at, fr-ch
  return input.toLowerCase();
};

export const mmuTranslateHandler = (
  translate: TranslateService,
  cookieService: CookieService,
  userService: MmuUserService
): () => Promise<void> => {
  const getLanguage = (): string => {
    // If the user is logged in, use the language from the user settings
    if (userService.user?.prefLang) {
      return userService.user?.prefLang;
    }

    // If the user is not logged in, use the language from the cookie
    if (cookieService.check(PREFERRED_LANGUAGE_COOKIE_NAME)) {
      return cookieService.get(PREFERRED_LANGUAGE_COOKIE_NAME);
    }

    // If neither the user nor the cookie has a language set, use the fallback language
    return FALLBACK_LANGUAGE_LOCALE;
  };

  /**
   * Update the language cookie with the preferred language of the user
   *
   * @param locale value stored in userDetails.prefLang : e.g., 'en-US'
   * */
  const updateLanguageCookie = (locale: string): void => {
    if (!locale) {
      cookieService.delete(
        PREFERRED_LANGUAGE_COOKIE_NAME,
        MmuCookiePreferencesService.cookiePath,
        undefined,
        location.protocol === 'https:',
        'Strict'
      );
    } else if (!cookieService.check(PREFERRED_LANGUAGE_COOKIE_NAME) || cookieService.get(PREFERRED_LANGUAGE_COOKIE_NAME) !== locale) {
      const expirationDate = new Date();
      expirationDate.setFullYear(expirationDate.getFullYear() + 1);

      cookieService.set(
        PREFERRED_LANGUAGE_COOKIE_NAME,
        locale,
        expirationDate,
        MmuCookiePreferencesService.cookiePath,
        undefined,
        location.protocol === 'https:',
        'Strict'
      );
    }
  };

  return async () => {

    /** Make sure every third-party plugin also reacts to language changes. */
    translate.onLangChange.subscribe((next) => {
      const lang = cookieService.check(PREFERRED_LANGUAGE_COOKIE_NAME) ? cookieService.get(PREFERRED_LANGUAGE_COOKIE_NAME) : next.lang;
      console.log(`i18n: App language changed to ${lang}.`);
      // Set new language in moment.js
      const language = mapLocaleToMomentjsLocale(lang);
      moment.locale(language);
    });

    // Set initial language to developer language
    translate.setDefaultLang(FALLBACK_LANGUAGE_LOCALE);

    const authenticated = await firstValueFrom(userService.ready$);

    if (authenticated) {
      updateLanguageCookie(userService.user.prefLang);
    }

    // Make sure to start app first when language is finally set and loaded
    await firstValueFrom(translate.use(getLanguage()));
  };
};
